import { Injectable, inject } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
  HttpContextToken,
} from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ConfigService } from 'src/app/pages/config/services/config.service';
import { AppConfig } from 'src/app/pages/config/app-config.interface';
import {
  NetworkService,
  ConnectionStatus,
} from '../shared/services/network.service';
import {
  OfflineManagerService,
  SKIP_OFFLINE_QUEUE,
} from '../shared/services/offline-manager.service';
import { RoleService } from '../shared/services/role.service';

/**
 * Marca una petición para que el interceptor NO añada el header
 * Authorization. Útil para endpoints que usan autenticación por
 * cookie de sesión (ej. generate-api-key).
 */
export const SKIP_AUTH = new HttpContextToken<boolean>(() => false);

const WRITE_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];

// https://stackoverflow.com/questions/69305555/angular-12-rxjs-7-angularfire-async-token-interceptor-with-deprecated-topromise
@Injectable()
export class InterceptorService implements HttpInterceptor {
  private configService = inject(ConfigService);
  private networkService = inject(NetworkService);
  private offlineManager = inject(OfflineManagerService);
  private roleService = inject(RoleService);

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return from(this.buildRequest(req)).pipe(
      switchMap((clonedReq) => next.handle(clonedReq)),
      catchError((error: HttpErrorResponse) => {
        // Las peticiones del replay offline gestionan su propia cola:
        // re-encolarlas aquí las duplicaría.
        if (req.context.get(SKIP_OFFLINE_QUEUE)) {
          return throwError(() => error);
        }

        // Encolar writes si estamos offline O si la red falló aunque el
        // navegador diga "online" (status 0: servidor caído, red cautiva,
        // timeout). navigator.onLine es notoriamente optimista.
        const isOffline =
          this.networkService.getCurrentNetworkStatus() ===
          ConnectionStatus.Offline;
        const isNetworkFailure = error.status === 0;

        // Política por rol:
        // - marketer (offline-first): siempre encola writes.
        // - admin (online-first): fail-fast (propaga el error).
        const shouldEnqueue =
          (isOffline || isNetworkFailure) &&
          WRITE_METHODS.includes(req.method) &&
          this.roleService.isMarketer();

        if (shouldEnqueue) {
          return from(
            this.offlineManager.storeRequest(req.url, req.method, req.body),
          ).pipe(switchMap(() => throwError(() => error)));
        }
        return throwError(() => error);
      }),
    );
  }

  private async buildRequest(req: HttpRequest<any>): Promise<HttpRequest<any>> {
    let headers = req.headers;

    // No sobreescribir Content-Type para FormData (multipart/form-data):
    // el navegador debe fijarlo con el boundary automaticamente.
    if (!(req.body instanceof FormData) && !headers.has('Content-Type')) {
      headers = headers.set('Content-Type', 'application/json');
    }

    // SKIP_AUTH: endpoints que usan cookie de sesión en vez de Bearer.
    if (!req.context.get(SKIP_AUTH) && !headers.has('Authorization')) {
      const config: AppConfig | null = await this.configService.getAppConfig().then();
      if (config?.api_key) {
        headers = headers.set('Authorization', `Bearer ${config.api_key}`);
      }
    }

    return req.clone({ headers });  // url is NEVER rewritten
  }
}
